import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import Locale from '@active/react-utils/i18n/Locale';
import Radio from '@active/react-ui/Radio';
import Form from '@active/react-ui/Form';
import FormGroup from '@active/react-ui/FormGroup';
import FormLabel from '@active/react-ui/FormLabel';
import FormField from '@active/react-ui/FormField';
import RadioGroup from '@active/react-ui/RadioGroup';
import uniq from 'lodash/uniq';
import WSS from '../../../../../../../shared/lib/wssAPI';
import { objectFilter } from '../../../../../../../shared/utils/commonUtils';
import pubSubInstance from '../../../../../../../shared/utils/pubSub';
import useCurrentAmountAndCreditBalance from '../../../../../../../shared/utils/currentAmountAndCreditBalance/useCurrentAmountAndCreditBalance';
import PinpadTipAndIcons from './PinpadTipAndIcons';

const Pinpad = () => {
  const [ isReady, setIsReady ] = useState(false);
  const [ paymentType, setPaymentType ] = useState('swipeCard');
  const { yourPayment } = useCurrentAmountAndCreditBalance();
  const {
    widgetNameSpace,
    CCZip,
    pinpadIdentifier,
    pinpadProcessor,
    isShowManualEntry,
    isShowCardType = true,
    pinpadCardTypes = [ 'CREDIT', 'DEBIT' ],
  } = useSelector(state => state.propsData);
  const validCardTypes = useMemo(() => uniq(pinpadCardTypes.filter(type => type === 'CREDIT' || type === 'DEBIT')));
  const isShowCardTypeSection = useMemo(() => {
    return isShowCardType && validCardTypes.length > 0;
  }, [ validCardTypes ]);
  const [ cardType, setCardType ] = useState(validCardTypes[0] || 'CREDIT');
  const {
    entities: {
      orderInfo: {
        identifier, orderDescriptor,
      } = {},
    },
  } = useSelector(state => state.mainStateData);
  const {
    entities: {
      paymentMethods: {
        pinPadInfo: { clientTransactionId } = {},
      },
    },
  } = useSelector(state => state.paymentMethods);

  const wssProcess = ({
    request, resolve, reject,
  }) => {
    WSS.sendWSPromise(request)
      .then(resolve)
      .catch(reject);
  };

  const checkAPDReady = useCallback(({ resolveCallback, commitCallback } = {}) => {
    const readyRequest = WSS.getWSRequest(
      WSS.RequestMethod.EXEC,
      WSS.FunctionName.IS_APD_READY,
      '',
      pinpadProcessor,
      pinpadIdentifier);
    wssProcess({
      request: readyRequest,
      resolve: (({ value }) => { // value is true|false
        if (value) {
          setIsReady(true);
          resolveCallback && resolveCallback(commitCallback);
        } else {
          commitCallback && commitCallback({ status: false, errorCode: 'PROCESSADPPAYMENT_FAILED' });
        }
      }),
      reject: (() => {
        setIsReady(false);
        commitCallback && commitCallback({ status: false, errorCode: 'PROCESSADPPAYMENT_FAILED' });
      }),
    });
  }, [ pinpadIdentifier, pinpadProcessor ]);

  const processADPPayment = useCallback((commitCallback) => {
    const initParameters = `isDebitCard=${cardType === 'DEBIT'}`;
    const processPaymentParameters = objectFilter({
      GenerateClientId: !clientTransactionId ? true : undefined,
      ClientTransactionID: clientTransactionId,
      PinpadDataEntryTimeout: '60',
      PinpadPINEntryTimeout: '60',
      PinpadSwipeCardTimeout: '60',
      OrderID: identifier && identifier.toString(),
      OrderAmount: yourPayment && yourPayment.toString(),
      OrderDescriptor: orderDescriptor && orderDescriptor.toString(),
      CCZip,
      ManualEntry: paymentType === 'manualEntry',
    });
    const paymentRequest = WSS.getWSRequestForJSON(
      WSS.RequestMethod.EXEC,
      WSS.FunctionName.PROCESS_APD_PAYMENT,
      initParameters,
      processPaymentParameters,
      pinpadProcessor,
      pinpadIdentifier);
    wssProcess({
      request: paymentRequest,
      resolve: ({ value: { amsResponse: { amstransactionID, userErrorMessage } } }) => {
        if (userErrorMessage.length > 0 && userErrorMessage !== 'Approved') {
          commitCallback({ status: false, errorCode: 'PROCESSADPPAYMENT_FAILED' });
        } else {
          commitCallback({ status: true, paymentId: amstransactionID, paymentMethodType: 'PINPAD' });
        }
      },
      reject: () => {
        commitCallback({ status: false, errorCode: 'PROCESSADPPAYMENT_FAILED' });
      },
    });
  }, [ CCZip, clientTransactionId, yourPayment, paymentType, cardType,
    identifier, orderDescriptor, pinpadIdentifier, pinpadProcessor ]);

  useEffect(() => {
    checkAPDReady();
  }, [ checkAPDReady ]);

  const handleSubmit = useCallback(() => {
    return new Promise((resolve) => {
      checkAPDReady({ resolveCallback: processADPPayment, commitCallback: resolve });
    });
  }, [ checkAPDReady, processADPPayment ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    };
  }, [ widgetNameSpace, handleSubmit ]);

  const handleChange = useCallback(({ values: { paymentType: paymentTypeValue, cardType: cardTypeValue } }) => {
    setPaymentType(paymentTypeValue);
    setCardType(cardTypeValue);
  }, []);

  return (
    <div className="pinpad cui-mg-t-20">
      <PinpadTipAndIcons isReady={isReady}/>
      {
        isReady
        && <Form
          className="pinpad-form cui-mg-t-20"
          initialValues={{ paymentType, cardType }}
          onChange={handleChange}
          customizeLayout>
          {
            isShowManualEntry
          && <FormGroup name="paymentType">
            <FormLabel>
              <Locale id="checkout.lib.components.common.paymentType" />
            </FormLabel>
            <FormField
              as={RadioGroup}>
              <Radio value="swipeCard">
                <Locale id="checkout.lib.components.common.swipeCard" />
              </Radio>
              <Radio value="manualEntry">
                <Locale id="checkout.lib.components.common.manualEntry" />
              </Radio>
            </FormField>
          </FormGroup>
          }
          { isShowCardTypeSection
          && <FormGroup name="cardType">
            <FormLabel>
              <Locale id="checkout.lib.components.common.cardType" />
            </FormLabel>
            <FormField as={RadioGroup}>
              {validCardTypes.map(type => (
                <Radio key={type} value={type}>
                  <Locale id={type === 'CREDIT' ? 'checkout.lib.components.common.paymentMethod.text.creditCard' : 'checkout.lib.components.common.debitCard'} />
                </Radio>
              ))}
            </FormField>
          </FormGroup>
          }
        </Form>
      }
    </div>
  );
};

export default Pinpad;
